import React, { Suspense, lazy, useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import type { ArceusMascotSize, ArceusMascotState } from './types';
import { ARCEUS_3D_ENABLED, ARCEUS_MASCOT_POSTER, ARCEUS_MASCOT_POSTER_SIZE } from './constants';
import '../../styles/arceus3d.css';

/**
 * The 3D Arceus mascot for the public site: the three.js GLB with the golden art painted on
 * (./ArceusMascotCanvas.tsx), whose head follows the pointer.
 *
 * Ported from opsManager components/arceus/mascot/arceus-mascot.tsx (PR #301) and stripped to a
 * 3D -> poster chain (no warp / 2.5D fallbacks, no pose mapping, no Tailwind):
 *  - first paint is always the still poster in a fixed-ratio box (no layout shift, works prerendered);
 *  - the canvas chunk (React.lazy) only arms when: REACT_APP_ARCEUS_3D is not "off", `animate`, no
 *    prefers-reduced-motion, WebGL is available, no data-saver / 2g, the element is within 200px of
 *    the viewport, and the browser is idle (requestIdleCallback, 1.2 s timeout);
 *  - the first painted frame adds `is-ready` to the root and the poster cross-fades out (styles/arceus3d.css);
 *  - any canvas failure (no context, context lost, asset 404, chunk load error) keeps the poster.
 */
export interface ArceusMascotProps {
  /** Accessible name of the figure, e.g. "Arceus, the JK Automations AI lead". */
  label: string;
  /** md = full figure (default, 768x1056 ratio); sm = head-and-chest crop (64px square). */
  size?: ArceusMascotSize;
  /** Allow the live canvas at all (default true). false = poster only. */
  animate?: boolean;
  /** With the canvas live: look toward the pointer / glance around on touch (default true). */
  follow?: boolean;
  /** Animation state (default "idle"); the six states are procedural and blend into each other. */
  state?: ArceusMascotState;
  className?: string;
}

const ArceusMascotCanvas = lazy(() => import('./ArceusMascotCanvas'));

const REDUCED_MQ = '(prefers-reduced-motion: reduce)';
function subscribeReduced(cb: () => void) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const mql = window.matchMedia(REDUCED_MQ);
  mql.addEventListener('change', cb);
  return () => mql.removeEventListener('change', cb);
}
const getReduced = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MQ).matches;
const getReducedServer = () => false;

let webglProbe: boolean | null = null;
/** Probed once per page: a throwaway context that is released straight away. */
function canUseWebGL(): boolean {
  if (webglProbe !== null) return webglProbe;
  try {
    const c = document.createElement('canvas');
    const gl = (c.getContext('webgl2') || c.getContext('webgl')) as WebGLRenderingContext | WebGL2RenderingContext | null;
    webglProbe = !!gl;
    gl?.getExtension('WEBGL_lose_context')?.loseContext();
  } catch {
    webglProbe = false;
  }
  return webglProbe;
}

function dataSaverOn(): boolean {
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  return !!conn && (conn.saveData === true || conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g');
}

interface BoundaryProps { onError: () => void; children: React.ReactNode }
/** A render/chunk-load error in the lazy canvas must never take the page down: report it and show nothing. */
class CanvasBoundary extends React.Component<BoundaryProps, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onError(); }
  render() { return this.state.failed ? null : this.props.children; }
}

type IdleWindow = Window & {
  requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
  cancelIdleCallback?: (id: number) => void;
};

export function ArceusMascot({ label, size = 'md', animate = true, follow = true, state = 'idle', className }: ArceusMascotProps) {
  const reduced = useSyncExternalStore(subscribeReduced, getReduced, getReducedServer);
  const [armed, setArmed] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const boxRef = useRef<HTMLSpanElement | null>(null);
  const onCanvasReady = useCallback(() => setReady(true), []);
  const onCanvasError = useCallback(() => { setReady(false); setFailed(true); }, []);

  useEffect(() => {
    if (armed || failed || !ARCEUS_3D_ENABLED || !animate || reduced || !canUseWebGL() || dataSaverOn()) return;
    const el = boxRef.current;
    const w = window as IdleWindow;
    let idleId: number | null = null;
    let timerId: number | null = null;
    const arm = () => {
      if (idleId !== null || timerId !== null) return;
      if (w.requestIdleCallback) idleId = w.requestIdleCallback(() => setArmed(true), { timeout: 1200 });
      else timerId = window.setTimeout(() => setArmed(true), 250);
    };
    let io: IntersectionObserver | null = null;
    if (el && typeof IntersectionObserver === 'function') {
      io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { io?.disconnect(); arm(); }
      }, { rootMargin: '200px' });
      io.observe(el);
    } else arm();
    return () => {
      io?.disconnect();
      if (idleId !== null) w.cancelIdleCallback?.(idleId);
      if (timerId !== null) window.clearTimeout(timerId);
    };
  }, [armed, animate, reduced, failed]);

  const live = ARCEUS_3D_ENABLED && animate && armed && !reduced && !failed;
  const is3d = live && ready;
  const poster = ARCEUS_MASCOT_POSTER_SIZE[size];
  const rootClass = `arceus3d arceus3d--${size}${is3d ? ' is-ready' : ''}${className ? ` ${className}` : ''}`;

  return (
    <span
      ref={boxRef}
      role="img"
      aria-label={label}
      data-arceus-mascot-render={is3d ? '3d' : 'poster'}
      className={rootClass}
    >
      <img
        className="arceus3d__poster"
        src={ARCEUS_MASCOT_POSTER[size]}
        alt={label}
        aria-hidden={is3d || undefined}
        width={poster.width}
        height={poster.height}
        decoding="async"
        draggable={false}
      />
      <span className="arceus3d__stage" aria-hidden="true">
        {live ? (
          <CanvasBoundary onError={onCanvasError}>
            <Suspense fallback={null}>
              <ArceusMascotCanvas state={state} size={size} follow={follow} onReady={onCanvasReady} onError={onCanvasError} />
            </Suspense>
          </CanvasBoundary>
        ) : null}
      </span>
    </span>
  );
}

export default ArceusMascot;
